<template>
  <div v-if="items.length > 0" class="recent-works-list">
    <p class="recent-works-list__heading">{{ $t("homePage.recent.title") }}</p>
    <ul class="recent-works-list__items">
      <li v-for="item in items" :key="item.key" class="recent-works-list__item-wrapper">
        <button type="button" class="recent-works-list__item" @click="onClickItem(item)">
          <span class="recent-works-list__thumbnail">
            <image-view class="recent-works-list__image" :src="item.thumbnailSrc" placeholder="/img/placeholders/novel.png" />
          </span>
          <span class="recent-works-list__info">
            <span ref="titleEls" class="recent-works-list__title">{{ displayTitle(item) }}</span>
            <span ref="subtitleEls" class="recent-works-list__subtitle">{{ displaySubtitle(item) }}</span>
          </span>
        </button>
      </li>
    </ul>
    <div v-if="isPc" class="recent-works-list__divider" />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import ImageView from "@/components/atoms/ImageView.vue";
import { getElementFont, truncateMiddleToWidth } from "@/lib/utils/textTruncate";
import { Novel, RecentlyEditedManuscript } from "@/lib/models";

// 中間省略記号
const MIDDLE_ELLIPSIS = "...";

export interface RecentWorkListItem {
  key: string;
  novelId: string;
  manuscriptKey: string;
  novelTitle: string;
  manuscriptTitle: string;
  thumbnailSrc?: string;
}

interface TruncatedText {
  title: string;
  subtitle: string;
}

export default Vue.extend({
  name: "RecentWorksList",
  components: {
    ImageView,
  },
  props: {
    limit: {
      type: Number,
      default: 3,
    },
    // 表示件数（limit）と同じ基準で区切り線の表示を切り替えるためのフラグ。
    // CSS の画面幅（mq）だと limit の UA 判定と基準がずれるため props で揃える
    isPc: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      truncated: {} as Record<string, TruncatedText>,
      resizeObserver: null as ResizeObserver | null,
      observedEl: null as Element | null,
      resizeRaf: null as number | null,
    };
  },
  computed: {
    rawItems(): RecentlyEditedManuscript[] {
      return this.$store.getters["recentlyEditedManuscriptsModule/items"];
    },
    items(): RecentWorkListItem[] {
      return this.rawItems.map((raw) => ({
        key: `${raw.novelId}:${raw.manuscriptKey}`,
        novelId: raw.novelId,
        manuscriptKey: raw.manuscriptKey,
        novelTitle: raw.novelTitle,
        manuscriptTitle: raw.manuscriptTitle,
        thumbnailSrc: this.thumbnailSrcFor(raw.novelId),
      }));
    },
  },
  watch: {
    items: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.updateTruncation();
          this.observeResize();
        });
      },
    },
  },
  async created() {
    try {
      await this.$store.dispatch("recentlyEditedManuscriptsModule/fetch", this.limit);
    } catch (e) {
      // 補助表示のため失敗時はセクション非表示のまま（エラーダイアログは GraphQLClient 側で表示済み）
      console.error("Failed to fetch recently edited manuscripts", e);
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.observedEl = null;
    if (this.resizeRaf !== null) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = null;
    }
  },
  methods: {
    thumbnailSrcFor(novelId: string): string | undefined {
      const novel = this.$store.getters["novelModule/novel"](novelId) as Novel | undefined;
      if (!novel || !novel.image) return undefined;
      // file: ソースはバージョン付きの元パスだとキャッシュが古くなるため、表紙の正規パスに揃える
      return novel.image.startsWith("file:") ? `file:novels/${novelId}/cover.jpg` : novel.image;
    },
    displayTitle(item: RecentWorkListItem): string {
      return this.truncated[item.key]?.title ?? item.novelTitle;
    },
    displaySubtitle(item: RecentWorkListItem): string {
      return this.truncated[item.key]?.subtitle ?? item.manuscriptTitle;
    },
    onClickItem(item: RecentWorkListItem) {
      this.$router.push({ name: "editor", params: { novelId: item.novelId, manuscriptKey: item.manuscriptKey } });
    },
    updateTruncation() {
      const titleEls = (this.$refs.titleEls as HTMLElement[] | undefined) || [];
      const subtitleEls = (this.$refs.subtitleEls as HTMLElement[] | undefined) || [];
      const next: Record<string, TruncatedText> = {};
      this.items.forEach((item: RecentWorkListItem, index: number) => {
        const titleEl = titleEls[index];
        const subtitleEl = subtitleEls[index];
        next[item.key] = {
          title: titleEl
            ? truncateMiddleToWidth(item.novelTitle, titleEl.clientWidth, getElementFont(titleEl), MIDDLE_ELLIPSIS)
            : item.novelTitle,
          subtitle: subtitleEl
            ? truncateMiddleToWidth(item.manuscriptTitle, subtitleEl.clientWidth, getElementFont(subtitleEl), MIDDLE_ELLIPSIS)
            : item.manuscriptTitle,
        };
      });
      this.truncated = next;
    },
    // 幅が変わると最適な省略位置も変わるため、サイドバーの表示/非表示切り替えなどの
    // リサイズ時に再計算する。v-if でルート要素が付け外しされた時だけ監視対象を張り直す
    // （items 更新のたびに張り直すと ResizeObserver の生成・破棄が連発してしまうため）
    observeResize() {
      if (this.$el === this.observedEl) {
        return;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      this.observedEl = null;
      if (typeof ResizeObserver === "undefined" || !(this.$el instanceof HTMLElement)) {
        return;
      }
      this.observedEl = this.$el;
      this.resizeObserver = new ResizeObserver(() => {
        // ResizeObserver のコールバック内で同期的に DOM を書き換えると、ブラウザの
        // 通知配送ループと競合し "ResizeObserver loop completed with undelivered
        // notifications" が発生するため、次フレームにずらして実行する
        if (this.resizeRaf !== null) {
          cancelAnimationFrame(this.resizeRaf);
        }
        this.resizeRaf = requestAnimationFrame(() => {
          this.resizeRaf = null;
          this.updateTruncation();
        });
      });
      this.resizeObserver.observe(this.$el);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.recent-works-list__heading {
  margin: 0 0 12px;
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
}

.recent-works-list__items {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.recent-works-list__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 16px 8px 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xs);
  background: transparent;
  color: var(--color-text-normal);
  text-align: left;
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.recent-works-list__thumbnail {
  display: block;
  flex-shrink: 0;
  width: 30px;
  height: 44px;
  overflow: hidden;
}

.recent-works-list__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.recent-works-list__info {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.recent-works-list__title {
  display: block;
  margin: 0;
  overflow: hidden;
  color: var(--color-text-normal);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.recent-works-list__subtitle {
  display: block;
  margin: 0;
  overflow: hidden;
  color: var(--color-text-low);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.recent-works-list__divider {
  height: 1px;
  margin-top: 16px;
  background-color: var(--color-border);
}
</style>
